import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const faqs = [
  {
    question: 'What currencies are available?',
    answer:
      "At the moment we support any transactions in USD, EUR, and GBP. If you'd like us to support your currency, please reach out and we'll work on it!",
  },
  {
    question: 'Who is Cap Gains for?',
    answer:
      'Cap Gains is targeted towards people who have investments in foreign currencies looking to calculate the capital gains in their home (target) currency. For example, expats who move abroad and maintain their brokerage account from their home country, or retirees who live abroad and live off their investments from their home country.',
  },
  {
    question: 'How can I calculate capital gains using Cap Gains?',
    answer:
      'To calculate capital gains using Cap Gains, simply upload your financial transaction data for foreign investments, including dividends, stocks, and currency exchanges. Our platform will provide instant results for the capital gains in your home currency.',
  },
]

const FAQPage: React.FC = () => {
  const navigate = useNavigate()
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className="sticky top-0 px-6 py-6 flex justify-between items-center z-50 shadow-md"
        style={{ backgroundColor: '#2C2B69' }}>
        <div className="container mx-auto flex justify-between items-center">
          <div
            className="text-2xl font-bold text-white cursor-pointer"
            onClick={() => navigate('/')}>
            CapitalGains Calculator
          </div>
          <div className="flex gap-4">
            <button
              onClick={() => navigate('/auth/login')}
              className="px-6 py-2 text-white hover:text-blue-200 transition">
              Login
            </button>
            <button
              onClick={() => navigate('/auth/signup')}
              className="px-6 py-2 bg-white rounded-lg hover:bg-blue-50 transition font-semibold"
              style={{ color: '#42419E' }}>
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* FAQ Content */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h1>
              <p className="text-xl text-gray-600">
                Find answers to common questions about Cap Gains
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleAccordion(index)}
                    className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors">
                    <span className="text-lg font-semibold text-gray-900">
                      {faq.question}
                    </span>
                    <svg
                      className={`w-6 h-6 text-gray-600 transition-transform duration-300 flex-shrink-0 ml-4 ${
                        openIndex === index ? 'transform rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openIndex === index ? 'max-h-96' : 'max-h-0'
                    }`}>
                    <div className="p-6 pt-0 text-gray-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Section */}
            <div className="mt-16 text-center">
              <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Still have questions?
                </h3>
                <p className="text-gray-600 mb-6">
                  Can't find the answer you're looking for? Please reach out to our
                  support team.
                </p>
                <button
                  onClick={() => navigate('/#contact')}
                  className="px-8 py-4 bg-[#2C2B69] text-white rounded-lg hover:bg-[#2C2B69]/80 transition font-semibold text-lg shadow-lg">
                  Contact Us
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-20 border-t border-gray-200 pt-12 pb-8 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                CapitalGains Calculator
              </h3>
              <p className="text-gray-600">
                Simplifying capital gains tax calculations for investors worldwide.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Product</h4>
              <ul className="space-y-2">
                <li>
                  <a
                    href="/#features"
                    className="text-gray-600 hover:text-blue-600 transition">
                    Features
                  </a>
                </li>
                <li>
                  <a
                    href="/#pricing"
                    className="text-gray-600 hover:text-blue-600 transition">
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="/faq" className="text-gray-600 hover:text-blue-600 transition">
                    FAQ
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Company</h4>
              <ul className="space-y-2">
                <li>
                  <a href="#" className="text-gray-600 hover:text-blue-600 transition">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-600 hover:text-blue-600 transition">
                    Blog
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-600 hover:text-blue-600 transition">
                    Careers
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <a
                    href="/privacy-policy"
                    className="text-gray-600 hover:text-blue-600 transition">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-600 hover:text-blue-600 transition">
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-600 hover:text-blue-600 transition">
                    Cookie Policy
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-8 text-center text-gray-600">
            <p>&copy; 2024 CapitalGains Calculator. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default FAQPage
